import { usePlatformAdmin } from '@/hooks/usePlatformAdmin';
import { Shield } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ROLE_LABELS } from '@/types/platformAdmin';

/**
 * Displays a subtle Shield icon in the header for platform administrators.
 * Shows admin role and permissions in a tooltip on hover.
 * Only visible to users with platform admin privileges.
 */
export function PlatformAdminBadge() {
  const { data: platformAdmin, isLoading } = usePlatformAdmin();

  if (isLoading || !platformAdmin?.isAdmin) return null;

  const roleLabel = platformAdmin.adminRole 
    ? ROLE_LABELS[platformAdmin.adminRole] 
    : 'Platform Admin';

  const getPermissionDescription = () => {
    switch (platformAdmin.adminRole) {
      case 'super_admin':
        return 'You have system-wide access to all organizations and platform settings.';
      case 'admin_manager':
        return 'You can view all organizations and require justification for sensitive actions.';
      case 'admin_consultant':
        return 'You have read-only platform access.';
      default:
        return 'You have platform administrative privileges.';
    }
  };

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <button
            className="flex items-center justify-center transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-warning focus:ring-offset-2 rounded-full p-1"
            aria-label="Platform Admin Mode"
          >
            <Shield 
              className="h-5 w-5 text-muted-foreground hover:text-warning transition-colors duration-150" 
              aria-hidden="true"
            />
          </button>
        </TooltipTrigger>
        <TooltipContent 
          className="max-w-[280px] p-4"
          role="tooltip"
          sideOffset={8}
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2 font-semibold">
              <Shield className="h-4 w-4 text-warning" />
              <span>Platform Admin Mode</span>
            </div>
            <div className="space-y-1">
              <p className="font-medium text-sm">{roleLabel}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {getPermissionDescription()}
              </p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
